import { Check, Clock3, Minus, TrendingUp, X } from 'lucide-react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';

function clampPercentage(value) {
  const num = Number(value);
  if (Number.isNaN(num)) return 0;
  return Math.max(0, Math.min(100, num));
}

function formatTime(value) {
  if (typeof value === 'string') return value;
  if (typeof value !== 'number' || Number.isNaN(value) || value < 0) return '00:00:00';

  const hours = Math.floor(value / 3600);
  const minutes = Math.floor((value % 3600) / 60);
  const seconds = Math.floor(value % 60);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function StatColumn({ icon, iconBorder, iconColor, iconBg, label, value, subValue }) {
  return (
    <Box
      sx={{
        px: { xs: 1.25, sm: 1.5, lg: 1.75 },
        py: { xs: 1, lg: 0.5 },
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: { xs: 32, lg: 34 },
          height: { xs: 32, lg: 34 },
          borderRadius: '9999px',
          border: `1px solid ${iconBorder}`,
          color: iconColor,
          backgroundColor: iconBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
        }}
      >
        {icon}
      </Box>

      <Typography sx={{ mt: 0.8, fontSize: { xs: 12, lg: 13 }, color: '#4b5563' }}>{label}</Typography>
      <Typography
        sx={{
          mt: 0.3,
          fontSize: { xs: 24, lg: 28 },
          lineHeight: 1,
          fontWeight: 600,
          color: '#111827',
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </Typography>
      <Typography sx={{ mt: 0.55, fontSize: { xs: 12, lg: 13 }, color: '#6b7280' }}>{subValue}</Typography>
    </Box>
  );
}

export default function ResultadosExamen({ resultsPayload, user }) {
  const summary = resultsPayload?.summary || {};
  const comparison = resultsPayload?.comparison || summary?.comparison || null;

  const studentName = user || 'Usuario';

  const totalQuestions = summary?.total_questions ?? summary?.settings?.actual_total_questions ?? 0;
  const correct = Number(summary?.correct_answers ?? 0);
  const incorrect = Number(summary?.incorrect_answers ?? 0);
  const unanswered = Number(summary?.unanswered_questions ?? 0);
  const usedTime = summary?.time_used_seconds ?? 0;
  const totalFromSummary = Number(totalQuestions ?? 0);
  const totalFromBreakdown = Math.max(0, correct + incorrect + unanswered);
  const resultsTotal = Math.max(totalFromSummary, totalFromBreakdown);

  // La puntuacion del resultado considera todo el examen, incluyendo sin responder.
  const scorePercent = resultsTotal > 0
    ? (correct / resultsTotal) * 100
    : Number(summary?.score_percentage ?? 0);

  const expectedTime =
    typeof summary?.duration_minutes === 'number' && summary?.duration_minutes > 0
      ? summary.duration_minutes * 60
      : null;

  const deltaPercent = comparison?.delta_percentage ?? null;
  const previousScoreLabel = comparison?.previous_label ?? '';

  const score = clampPercentage(scorePercent);
  const roundedScore = Math.round(score);
  const displayedScore = roundedScore >= 100 ? 100 : Math.min(99, roundedScore);
  const summaryText =
    resultsPayload?.summaryText ||
    summary?.status_message ||
    (score >= 70
      ? 'Tu desempeno estuvo por encima del promedio.'
      : 'Sigue practicando, vas por buen camino.');

  const hasDelta = deltaPercent !== null && deltaPercent !== undefined;
  const isDeltaPositive = Number(deltaPercent) >= 0;
  const deltaSign = isDeltaPositive ? '+' : '';

  const correctPct = resultsTotal > 0 ? Math.round((correct / resultsTotal) * 100) : 0;
  const incorrectPct = resultsTotal > 0 ? Math.round((incorrect / resultsTotal) * 100) : 0;
  const unansweredPct = resultsTotal > 0 ? Math.round((unanswered / resultsTotal) * 100) : 0;

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          border: '1px solid #e5e7eb',
          borderRadius: '14px',
          p: { xs: 1.25, sm: 1.5 },
          backgroundColor: '#fff',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(330px, 1.15fr) minmax(470px, 1fr)' },
            alignItems: 'stretch',
            gap: { xs: 1.25, lg: 0 },
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '120px 1fr' },
              alignItems: 'center',
              gap: { xs: 1.2, sm: 1.8 },
              pr: { lg: 2 },
              borderRight: { lg: '1px solid #e5e7eb' },
            }}
          >
            <Box sx={{ px: 1, display: 'flex', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
              <Box sx={{ position: 'relative', width: { xs: 102, sm: 112 }, height: { xs: 102, sm: 112 } }}>
                <CircularProgress
                  variant="determinate"
                  value={100}
                  size="100%"
                  thickness={5}
                  sx={{ color: '#e5e7eb', position: 'absolute', left: 0, top: 0 }}
                />
                <CircularProgress
                  variant="determinate"
                  value={score}
                  size="100%"
                  thickness={5}
                  sx={{ color: '#2563eb', position: 'absolute', left: 0, top: 0 }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: { xs: 22, sm: 24 }, lineHeight: 1, fontWeight: 700, color: '#111827' }}>
                    {displayedScore}%
                  </Typography>
                  <Typography sx={{ mt: 0.25, fontSize: 11, color: '#6b7280' }}>Puntuacion</Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ px: { xs: 0.5, sm: 0 } }}>
              <Typography sx={{ fontSize: { xs: 22, sm: 28, lg: 30 }, lineHeight: 1.1, fontWeight: 600, color: '#111827' }}>
                Buen trabajo, {studentName}!
              </Typography>
              <Typography sx={{ mt: 0.5, fontSize: { xs: 12, sm: 14, lg: 16 }, color: '#4b5563' }}>{summaryText}</Typography>

              {hasDelta && (
                <Box
                  sx={{
                    mt: 1.1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1.1,
                    border: '1px solid #c7d2fe',
                    backgroundColor: '#eef2ff',
                    borderRadius: '12px',
                    px: 1.3,
                    py: 0.75,
                  }}
                >
                  <TrendingUp size={14} color="#4f46e5" />
                  <Box>
                    <Typography sx={{ fontSize: { xs: 12, sm: 13, lg: 14 }, lineHeight: 1.1, fontWeight: 700, color: '#4f46e5' }}>
                      {deltaSign}
                      {deltaPercent}% vs tu ultimo simulador
                    </Typography>
                    <Typography sx={{ mt: 0.2, fontSize: { xs: 11, sm: 12 }, color: '#6b7280' }}>{previousScoreLabel}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(4, minmax(0, 1fr))',
              },
              alignItems: 'stretch',
              gap: { xs: 1, sm: 1.1, lg: 0 },
              '& > *': {
                border: { xs: '1px solid #e5e7eb', lg: 'none' },
                borderRadius: { xs: '10px', lg: 0 },
                backgroundColor: { xs: '#ffffff', lg: 'transparent' },
              },
              '& > * + *': {
                borderLeft: { lg: '1px solid #e5e7eb' },
              },
            }}
          >
            <StatColumn
              icon={<Check size={16} />}
              iconBorder="#86efac"
              iconColor="#22c55e"
              iconBg="#f0fdf4"
              label="Aciertos"
              value={`${correct}${totalQuestions > 0 ? ` / ${totalQuestions}` : ''}`}
              subValue={`${correctPct}%`}
            />

            <StatColumn
              icon={<X size={16} />}
              iconBorder="#fda4af"
              iconColor="#f43f5e"
              iconBg="#fff1f2"
              label="Incorrectas"
              value={incorrect}
              subValue={`${incorrectPct}%`}
            />

            <StatColumn
              icon={<Minus size={16} />}
              iconBorder="#fcd34d"
              iconColor="#f59e0b"
              iconBg="#fffbeb"
              label="Sin responder"
              value={unanswered}
              subValue={`${unansweredPct}%`}
            />

            <StatColumn
              icon={<Clock3 size={16} />}
              iconBorder="#a5b4fc"
              iconColor="#4f46e5"
              iconBg="#eef2ff"
              label="Tiempo utilizado"
              value={formatTime(usedTime)}
              subValue={expectedTime === null ? 'sin limite' : `de ${formatTime(expectedTime)} hrs`}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
